/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { FaXmark } from "react-icons/fa6";
import {
  appliedDiscountListApi,
  appliedLoyaltyDiscountListApi,
  applyDiscountApi,
  applyLoyaltyDiscountApi,
  removeDiscountApi,
  removeLoyaltyDiscountApi,
} from "@/api/take-order/take-order";
import { getAllDiscountApi } from "@/api/settings/discount";
import useAuth from "@/hooks/useAuth";
import { getActiveRestaurantId } from "@/lib/idProvider";

function getUpdatedOrderFromResponse(response) {
  return response?.data?.order || response?.data || null;
}

function normalizeList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function getListFromResponse(response, keys = []) {
  const payload = response?.data;
  const candidates = [payload?.data, payload];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;

    if (candidate && typeof candidate === "object") {
      for (const key of keys) {
        if (Array.isArray(candidate?.[key])) return candidate[key];
      }
    }
  }

  return null;
}

function isSuccessResponse(response) {
  return response?.status === "success" || response?.status === true || response?.success === true;
}

export default function LoyaltyDiscountPopup({
  orderId,
  orderData,
  isTabletView,
  isCompleted,
  availableDiscounts = [],
  appliedDiscounts = [],
  appliedLoyaltyDiscounts = [],
  onOrderUpdated,
  onClose,
}) {
  const { user } = useAuth();
  const reduxOrderData = useSelector((state) => state.takeOrder?.order?.orderData);
  const reduxDiscountList = useSelector((state) => state.discount?.discountList || state.discount?.allDiscounts || []);

  const [selectedDiscountCode, setSelectedDiscountCode] = useState("");
  const [loyaltyCode, setLoyaltyCode] = useState("");
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [isApplyingLoyalty, setIsApplyingLoyalty] = useState(false);
  const [removingKey, setRemovingKey] = useState("");
  const [discountItems, setDiscountItems] = useState(normalizeList(appliedDiscounts));
  const [loyaltyItems, setLoyaltyItems] = useState(normalizeList(appliedLoyaltyDiscounts));
  const [directDiscounts, setDirectDiscounts] = useState(availableDiscounts);
  const hasFetchedDiscountsRef = useRef(false);

  const activeOrder = orderData || reduxOrderData;
  const restaurantId = activeOrder?.restaurant_id || user?.restaurant_id || getActiveRestaurantId();

  useEffect(() => {
    if (Array.isArray(availableDiscounts) && availableDiscounts.length > 0) {
      setDirectDiscounts(availableDiscounts);
      return;
    }
    if (Array.isArray(reduxDiscountList) && reduxDiscountList.length > 0) {
      setDirectDiscounts(reduxDiscountList);
      return;
    }

    if (hasFetchedDiscountsRef.current) return;
    if (!restaurantId) return;

    hasFetchedDiscountsRef.current = true;

    getAllDiscountApi(restaurantId, {
      per_page: 50,
      status: "active",
    }).then((res) => {
      if (res?.status === "success") {
        const discounts = res?.data?.discounts || res?.data || [];
        setDirectDiscounts(discounts);
      }
    }).catch((err) => {
      console.error("Failed to fetch discounts in LoyaltyDiscountPopup:", err);
    });
  }, [
    availableDiscounts,
    reduxDiscountList,
    restaurantId,
  ]);

  const effectiveDiscounts = useMemo(() => {
    if (Array.isArray(directDiscounts) && directDiscounts.length > 0) return directDiscounts;
    if (Array.isArray(availableDiscounts) && availableDiscounts.length > 0) return availableDiscounts;
    if (Array.isArray(reduxDiscountList) && reduxDiscountList.length > 0) return reduxDiscountList;
    return [];
  }, [directDiscounts, availableDiscounts, reduxDiscountList]);

  const refreshAppliedDiscountData = useCallback(async () => {
    if (!orderId) return;

    try {
      const [discountResponse, loyaltyResponse] = await Promise.all([
        appliedDiscountListApi(orderId),
        appliedLoyaltyDiscountListApi(orderId),
      ]);

      if (isSuccessResponse(discountResponse)) {
        const discountList = getListFromResponse(discountResponse, ["discounts", "order_discounts"]);
        if (discountList !== null) {
          setDiscountItems(normalizeList(discountList));
        }
      }

      if (isSuccessResponse(loyaltyResponse)) {
        const loyaltyList = getListFromResponse(loyaltyResponse, ["loyalty_discounts", "applied_loyalty_discounts"]);
        if (loyaltyList !== null) {
          setLoyaltyItems(normalizeList(loyaltyList));
        }
      }
    } catch (error) {
      // Keep UI usable with existing state when refresh fails.
    }
  }, [orderId]);

  useEffect(() => {
    const nextItems = normalizeList(appliedDiscounts);
    if (nextItems.length > 0) {
      setDiscountItems(nextItems);
    }
  }, [appliedDiscounts]);

  useEffect(() => {
    const nextItems = normalizeList(appliedLoyaltyDiscounts);
    if (nextItems.length > 0) {
      setLoyaltyItems(nextItems);
    }
  }, [appliedLoyaltyDiscounts]);

  useEffect(() => {
    refreshAppliedDiscountData();
  }, [refreshAppliedDiscountData]);

  const discountTotal = useMemo(() => {
    return discountItems.reduce((total, item) => total + (Number(item?.discount_amount) || 0), 0);
  }, [discountItems]);

  const loyaltyTotal = useMemo(() => {
    return loyaltyItems.reduce(
      (total, item) => total + (Number(item?.loyalty_discount_amount) || 0),
      0,
    );
  }, [loyaltyItems]);

  async function applyDiscount() {
    if (isCompleted) return toast.error("Order is completed. No changes allowed");
    if (!selectedDiscountCode) return toast.error("Please select a discount");

    setIsApplyingDiscount(true);
    try {
      const response = await applyDiscountApi(orderId, { code: selectedDiscountCode });
      if (response?.status === "success") {
        const updatedOrder = getUpdatedOrderFromResponse(response);
        if (updatedOrder) onOrderUpdated(updatedOrder);
        await refreshAppliedDiscountData();
        setSelectedDiscountCode("");
        toast.success("Discount applied successfully");
      } else {
        toast.error(response?.message || "Failed to apply discount");
      }
    } catch (error) {
      toast.error("Failed to apply discount");
    } finally {
      setIsApplyingDiscount(false);
    }
  }

  async function applyLoyalty() {
    if (isCompleted) return toast.error("Order is completed. No changes allowed");
    if (!loyaltyCode.trim()) return toast.error("Please enter loyalty code");

    setIsApplyingLoyalty(true);
    try {
      const response = await applyLoyaltyDiscountApi(orderId, {
        loyalty_discount_code: loyaltyCode.trim(),
      });
      if (response?.status === "success") {
        const updatedOrder = getUpdatedOrderFromResponse(response);
        if (updatedOrder) onOrderUpdated(updatedOrder);
        await refreshAppliedDiscountData();
        setLoyaltyCode("");
        toast.success("Loyalty discount applied successfully");
      } else {
        toast.error(response?.message || "Failed to apply loyalty discount");
      }
    } catch (error) {
      toast.error("Failed to apply loyalty discount");
    } finally {
      setIsApplyingLoyalty(false);
    }
  }

  async function removeDiscount(discountItem) {
    if (isCompleted) return toast.error("Order is completed. No changes allowed");

    const discountId = discountItem?.id || discountItem?.order_discount_id || discountItem?.discount_id;
    if (!discountId) return toast.error("Cannot remove discount: missing id");

    setRemovingKey(`discount-${discountId}`);
    try {
      const response = await removeDiscountApi(discountId, orderId);
      if (response?.status === "success") {
        const updatedOrder = getUpdatedOrderFromResponse(response);
        if (updatedOrder) onOrderUpdated(updatedOrder);
        await refreshAppliedDiscountData();
        toast.success("Discount removed successfully");
      } else {
        toast.error(response?.message || "Failed to remove discount");
      }
    } catch (error) {
      toast.error("Failed to remove discount");
    } finally {
      setRemovingKey("");
    }
  }

  async function removeLoyalty(loyaltyItem) {
    if (isCompleted) return toast.error("Order is completed. No changes allowed");

    const loyaltyId =
      loyaltyItem?.id ||
      loyaltyItem?.order_loyalty_discount_id ||
      loyaltyItem?.loyalty_discount_id;
    if (!loyaltyId) return toast.error("Cannot remove loyalty discount: missing id");

    setRemovingKey(`loyalty-${loyaltyId}`);
    try {
      const response = await removeLoyaltyDiscountApi(loyaltyId);
      if (response?.status === "success") {
        const updatedOrder = getUpdatedOrderFromResponse(response);
        if (updatedOrder) onOrderUpdated(updatedOrder);
        await refreshAppliedDiscountData();
        toast.success("Loyalty discount removed successfully");
      } else {
        toast.error(response?.message || "Failed to remove loyalty discount");
      }
    } catch (error) {
      toast.error("Failed to remove loyalty discount");
    } finally {
      setRemovingKey("");
    }
  }

  console.log("loyalty discount", loyaltyItems);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70">
      <div
        className={`relative ${isTabletView ? "w-[680px]" : "w-[900px]"} bg-color2 border border-color3 rounded-md p-5`}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-[1px] right-[1px] text-red-500 hover:text-red-400"
        >
          <FaXmark size={20} />
        </button>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-color3  rounded p-3 min-h-[330px] flex flex-col">
            <h3 className="font-semibold mb-3 text-lg text-center">Discount</h3>
            <div className="flex gap-2">
              <select
                value={selectedDiscountCode}
                onChange={(e) => setSelectedDiscountCode(e.target.value)}
                disabled={isCompleted || isApplyingDiscount}
                className="w-full py-2 px-2 bg-color2  rounded outline-none"
              >
                <option value="">Select discount</option>
                {effectiveDiscounts.map((discount, idx) => {
                  const code = discount?.code || discount?.short_code || discount?.name || "";
                  const displayValue =
                    discount?.discount_value !== undefined && discount?.discount_value !== null
                      ? ` (${discount.discount_type === "percentage" ? `${discount.discount_value}%` : `£${Number(discount.discount_value || 0).toFixed(2)}`})`
                      : "";
                  return (
                    <option key={discount?.id || code || idx} value={code} disabled={!code}>
                      {discount?.name || "Discount"}{displayValue}
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                onClick={applyDiscount}
                disabled={isCompleted || isApplyingDiscount}
                className="px-4 py-2 bg-green-500 rounded font-semibold disabled:opacity-60"
              >
                {isApplyingDiscount ? "..." : "Apply"}
              </button>
            </div>

            <div className="mt-4 flex-1 overflow-y-auto space-y-2">
              {discountItems.length === 0 && (
                <div className="text-md text-color7">No discounts applied</div>
              )}
              {discountItems.map((item, index) => {
                const id = item?.id || item?.order_discount_id || item?.discount_id || index;
                const key = `discount-${id}`;
                return (
                  <div key={key} className="flex items-center justify-between text-md">
                    <button
                      type="button"
                      onClick={() => removeDiscount(item)}
                      disabled={isCompleted || removingKey === key}
                      className="text-red-500 pr-2 disabled:opacity-60"
                    >
                      <FaXmark size={12} />
                    </button>
                    <span className="flex-1 truncate">{item?.discount_name || item?.name || "Discount"}</span>
                    <span>£ {Number(item?.discount_amount || 0).toFixed(2)}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 font-semibold text-amber-400 text-md">
              Discount Total : £ {discountTotal.toFixed(2)}
            </div>
          </div>

          <div className="bg-color3 rounded p-3 min-h-[530px] flex flex-col">
            <h3 className="font-semibold mb-3 text-lg text-center">Loyalty Code</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={loyaltyCode}
                onChange={(e) => setLoyaltyCode(e.target.value.toUpperCase())}
                disabled={isCompleted || isApplyingLoyalty}
                placeholder="Enter Code"
                className="w-full py-2 px-2 bg-color2 rounded outline-none"
              />
              <button
                type="button"
                onClick={applyLoyalty}
                disabled={isCompleted || isApplyingLoyalty}
                className="px-4 py-2 bg-green-500 rounded font-semibold disabled:opacity-60"
              >
                {isApplyingLoyalty ? "..." : "Apply"}
              </button>
            </div>

            <div className="mt-4 flex-1 overflow-y-auto space-y-2">
              {loyaltyItems.length === 0 && (
                <div className="text-md text-color7">No loyalty discounts applied</div>
              )}
              {loyaltyItems.map((item, index) => {
                const id = item?.id || item?.order_loyalty_discount_id || item?.loyalty_discount_id || index;
                const key = `loyalty-${id}`;
                return (
                  <div key={key} className="flex items-center justify-between text-md">
                    <button
                      type="button"
                      onClick={() => removeLoyalty(item)}
                      disabled={isCompleted || removingKey === key}
                      className="text-red-500 pr-2 disabled:opacity-60"
                    >
                      <FaXmark size={12} />
                    </button>
                    <span className="flex-1 truncate">{item?.loyalty_short_code || item?.loyalty_discount_code || "Code"}</span>
                    {
                      (item.free_items && item.free_items.length > 0) ? (
                        <div className="pl-5 mt-1 flex flex-col gap-1">
                          {item.free_items.map((freeItem, fIndex) => (
                            <div key={freeItem?.id || fIndex} className="flex items-center text-xs text-green-400 gap-1.5 bg-green-500/5 py-1 px-1.5 rounded border border-green-500/10">
                              <span className="bg-green-500/20 text-green-400 border border-green-500/30 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider shrink-0">
                                Free
                              </span>
                              <span className="truncate font-medium">
                                {freeItem?.name || "Item"}
                                {freeItem?.variant?.name && ` (${freeItem.variant.name})`}
                              </span>
                            </div>
                          ))}
                        </div>
                      ):
                      <span>£ {Number(item?.loyalty_discount_amount || 0).toFixed(2)}</span>
                    }
                    
                  </div>
                );
              })}
            </div>

            <div className="mt-3 font-semibold text-amber-400 text-md">
              Loyalty Discount Total : £ {loyaltyTotal.toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
